import { useEffect } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  CheckCircle,
  ClipboardCheck,
  History,
  Settings,
  Stethoscope,
  User,
  Wifi,
  WifiOff,
} from 'lucide-react';
import { Toaster } from 'sonner';
import { cn } from '@/lib/utils';
import { useAuth } from '@/lib/auth-context';
import { useBranding } from '@/lib/use-cobrand';
import { useSSE } from '@/hooks/use-sse';
import { usePushNotifications } from '@/hooks/use-push-notifications';
import { useNotificationSound } from '@/hooks/use-notification-sound';
import { InstallPrompt } from '@/components/install-prompt';
import { useQueryClient } from '@tanstack/react-query';

const navItems = [
  { to: '/approvals', icon: ClipboardCheck, label: 'Approvals' },
  { to: '/intake', icon: Stethoscope, label: 'Intake' },
  { to: '/confirmed', icon: CheckCircle, label: 'Confirmed' },
  { to: '/history', icon: History, label: 'History' },
  { to: '/settings', icon: Settings, label: 'Settings' },
];

// What each role actually has server-side access to — the nav never offers a
// tab whose API would 403 (dept heads/doctors: their approval queue;
// coordinators add the intake desk; the rep sees everything).
const NAV_BY_ROLE: Record<string, string[]> = {
  hospital_rep: ['/approvals', '/intake', '/confirmed', '/history', '/settings'],
  coordinator: ['/approvals', '/intake', '/settings'],
  intake_coordinator: ['/intake'],
  department_head: ['/approvals', '/settings'],
  doctor: ['/approvals', '/settings'],
};

const SSE_EVENTS: string[] = ['approval_needed', 'decision_made'];

export function AppShell() {
  const { user } = useAuth();
  const isIntakeOnly = user?.role === 'intake_coordinator';
  const allowed = NAV_BY_ROLE[user?.role ?? ''] ?? ['/approvals', '/settings'];
  const visibleNavItems = navItems.filter((item) => allowed.includes(item.to));
  const brand = useBranding();
  const queryClient = useQueryClient();
  const { setupForegroundListener } = usePushNotifications();
  const { playSound } = useNotificationSound();

  useEffect(() => {
    if (user && !isIntakeOnly) {
      setupForegroundListener();
    }
  }, [user, isIntakeOnly, setupForegroundListener]);

  const { isConnected } = useSSE({
    enabled: !!user && !isIntakeOnly,
    events: SSE_EVENTS,
    onMessage: (_data, eventType) => {
      queryClient.invalidateQueries({ queryKey: ['approvals'] });
      if (eventType === 'approval_needed') {
        playSound();
      }
    },
  });

  return (
    <div className="min-h-dvh bg-background">
      {/* ── Desktop Sidebar ─────────────────────────────── */}
      <aside className="hidden lg:fixed lg:inset-y-0 lg:left-0 lg:z-50 lg:flex lg:w-64 lg:flex-col bg-card border-r border-border">
        {/* Subtle warm glow at top */}
        <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-primary-50/40 to-transparent pointer-events-none" />

        {/* Branding — co-brand: group's logo/name, RefDash retained below */}
        <div className="relative flex items-center gap-3 px-6 h-16 border-b border-border shrink-0">
          <img src={brand.logo_url ?? `${import.meta.env.BASE_URL}logo.svg`} alt={brand.display_name} className="w-9 h-9 rounded-xl object-contain" />
          <div className="min-w-0">
            <h1 className="font-sans text-sm font-bold text-foreground leading-none truncate">
              {brand.display_name}
            </h1>
            <p className="text-[11px] text-muted-foreground leading-tight mt-0.5 truncate">
              {brand.tenant ? 'Powered by RefDash' : (user?.hospital_name ?? user?.hospital_id)}
            </p>
          </div>
        </div>

        {/* Navigation */}
        <nav className="relative flex-1 px-3 py-5 space-y-1 overflow-y-auto">
          {visibleNavItems.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                cn(
                  'sidebar-nav-item flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium',
                  isActive
                    ? 'bg-primary-50 text-primary font-semibold'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/60',
                )
              }
            >
              {({ isActive }) => (
                <>
                  <Icon
                    className="w-[18px] h-[18px] shrink-0"
                    strokeWidth={isActive ? 2.25 : 1.75}
                  />
                  <span>{label}</span>
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Bottom: connection + user */}
        <div className="relative border-t border-border px-5 py-4 space-y-3 shrink-0">
          <div
            className={cn(
              'flex items-center gap-2 text-xs font-medium',
              isConnected ? 'text-emerald-600' : 'text-stone-400',
            )}
          >
            {isConnected ? (
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
              </span>
            ) : (
              <WifiOff className="w-3.5 h-3.5" />
            )}
            <span>{isConnected ? 'Connected' : 'Offline'}</span>
          </div>

          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
              <User className="w-4 h-4 text-primary" />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-semibold text-foreground truncate">
                {user?.display_name}
              </p>
              <p className="text-[10px] text-muted-foreground truncate">
                {user?.username}
              </p>
            </div>
          </div>
        </div>
      </aside>

      {/* ── Main Content Area ───────────────────────────── */}
      <div className="lg:pl-64 flex flex-col min-h-dvh">
        {/* Mobile top bar (hidden on desktop) */}
        <header className="sticky top-0 z-40 bg-card/80 backdrop-blur-xl border-b border-border lg:hidden">
          <div
            className="flex items-center justify-between h-14 px-5"
            style={{ paddingTop: 'var(--safe-top)' }}
          >
            <div className="flex items-center gap-3">
              <img src={brand.logo_url ?? `${import.meta.env.BASE_URL}logo.svg`} alt={brand.display_name} className="w-8 h-8 rounded-lg object-contain" />
              <div>
                <h1 className="font-sans text-sm font-bold text-foreground leading-none truncate">
                  {brand.display_name}
                </h1>
                <p className="text-[11px] text-muted-foreground leading-tight mt-0.5">
                  {brand.tenant ? 'Powered by RefDash' : (user?.hospital_name ?? user?.hospital_id)}
                </p>
              </div>
            </div>

            <div
              className={cn(
                'flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full',
                isConnected
                  ? 'text-emerald-700 bg-emerald-50'
                  : 'text-stone-400 bg-stone-100',
              )}
            >
              {isConnected ? (
                <Wifi className="w-3.5 h-3.5" />
              ) : (
                <WifiOff className="w-3.5 h-3.5" />
              )}
              <span>{isConnected ? 'Live' : 'Offline'}</span>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 mb-nav">
          <Outlet />
        </main>

        {/* Mobile bottom tab bar (hidden on desktop) */}
        <nav
          className="fixed bottom-0 inset-x-0 z-50 bg-card/90 backdrop-blur-xl border-t border-border lg:hidden"
          style={{ paddingBottom: 'var(--safe-bottom)' }}
        >
          <div className="flex items-center justify-around h-16 max-w-md mx-auto">
            {visibleNavItems.map(({ to, icon: Icon, label }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  cn(
                    'flex flex-col items-center gap-0.5 py-1.5 px-4 rounded-xl transition-colors duration-150 relative',
                    isActive
                      ? 'text-primary'
                      : 'text-muted-foreground hover:text-foreground',
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <div className="relative">
                      <Icon
                        className="w-[22px] h-[22px]"
                        strokeWidth={isActive ? 2.25 : 1.75}
                      />
                      {isActive && (
                        <motion.div
                          layoutId="tab-indicator"
                          className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-primary"
                          transition={{ type: 'spring', stiffness: 500, damping: 35 }}
                        />
                      )}
                    </div>
                    <span className="text-[10px] font-semibold">{label}</span>
                  </>
                )}
              </NavLink>
            ))}
          </div>
        </nav>
      </div>

      <InstallPrompt />

      <Toaster
        position="top-center"
        toastOptions={{
          className: 'font-body',
          style: {
            borderRadius: '0.75rem',
            border: '1px solid var(--color-border)',
          },
        }}
      />
    </div>
  );
}
